import React, { useState, useEffect } from 'react';
import { formatNumber, formatRank } from '../../utils/formatting';
import { getScoreColor } from '../../utils/coloring';
import { sortData, requestSort, getSortIcon } from '../../utils/sorting';
import { getCrimeDisplayName, weightedPercentages } from '../../constants/crimes';
import { getStateAbbreviation, isStateScopedCityCountyAggregate } from '../../constants/locations';
import { useMenuState, useMobileState } from '../../hooks/tables';
import { ChevronDown } from 'lucide-react';
import ScoreMethodologyModal from '../Modals/ScoreMethodologyModal';
import './Tables.css';
import './LocationType.css';

const LocationTypeScoresTable = ({ 
  data,
  locationType,
  onLocationSelect,
  selectedLocation,
  selectedCrime,
  selectedSource,
  sortConfig,
  onSortChange,
  currentPage,
  itemsPerPage,
  compareMode,
  onCompareModeChange,
  setHoveredLocation
}) => {
  const [isMethodologyModalOpen, setIsMethodologyModalOpen] = useState(false);
  const { menuOpen, setMenuOpen, toggleMenu } = useMenuState();
  const isMobile = useMobileState();

  useEffect(() => {
    const urlTableSort = new URLSearchParams(window.location.search).get('table_sort');
    if (urlTableSort && sortConfig.key === urlTableSort) {
      return;
    }

    const currentKey = sortConfig.key;
    
    if (currentKey && (currentKey.includes('_score') || currentKey.includes('composite_score'))) {
      let expectedKey;
      
      if (currentKey.includes('composite_score_rank')) {
        expectedKey = compositeScoreRankField;
      } else if (currentKey.includes('composite_score')) {
        expectedKey = compositeScoreField;
      } else if (currentKey.includes('_score_rank')) {
        expectedKey = scoreRankField;
      } else if (currentKey.includes('_score')) {
        expectedKey = scoreField;
      }
      
      if (expectedKey && currentKey !== expectedKey) {
        onSortChange({
          key: expectedKey,
          direction: sortConfig.direction
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, sortConfig.key]);

  const isMetro = locationType === 'metro';
  const isCounty = locationType === 'county';
  const isCity = locationType === 'city';
  const isCountyOrCity = isCounty || isCity;
  const isOneCountyOrCity = data?.locations?.[0]?.total_counties_state === 1 || data?.locations?.[0]?.total_cities_state === 1; 
  const isAllCountiesNational = isCounty && selectedLocation?.info?.name === 'All Counties';
  const isAllCitiesNational = isCity && selectedLocation?.info?.name === 'All Cities';
  const isAllNational = isAllCountiesNational || isAllCitiesNational;
  
  const selectedState = selectedLocation?.info?.state;
  const dataState = data?.locations?.[0]?.state;
  const dataHasMultipleStates = data?.locations?.length > 1 && 
                                data.locations.some(loc => loc.state !== data.locations[0].state);
  const dataIsNational = isCountyOrCity && dataHasMultipleStates;
  const selectedIsNational = !selectedState;
  const bothAreState = selectedState && dataState && !dataIsNational;
  const bothAreNational = selectedIsNational && dataIsNational;
  const dataMatchesSelection = bothAreNational || bothAreState || (selectedState === dataState);
  const isStateScopedAggregate = isStateScopedCityCountyAggregate(selectedLocation);

  const showComparisonDropdown =
    isCountyOrCity &&
    !isOneCountyOrCity &&
    !isAllNational &&
    (dataMatchesSelection || (isStateScopedAggregate && !dataIsNational));
  
  const locationPrefix = locationType.charAt(0).toUpperCase() + locationType.slice(1);
  const crimePrefix = selectedCrime.toLowerCase();

  let stateAbbr = '';
  if (isCountyOrCity && data?.locations?.length > 0) {
    stateAbbr = getStateAbbreviation(data.locations[0]?.state || '');
  }
  
  const getFieldKey = (baseField) => {
    if (!isCountyOrCity) return `${crimePrefix}_${baseField}`;
    
    return compareMode === 'state' 
      ? `${crimePrefix}_state_${baseField}` 
      : `${crimePrefix}_national_${baseField}`;
  };
  
  const getCompositeFieldKey = (baseField) => {
    if (!isCountyOrCity) return `${baseField}`;
    
    return compareMode === 'state'
      ? `state_${baseField}`
      : `national_${baseField}`;
  };

  const formatLocationName = (location) => {
    if (isCounty) {
      return (location.county === 'Carson City' && location.state === 'Nevada') ?
        `${location.county}, ${getStateAbbreviation(location.state)}` :
        location.state === 'Louisiana' ?
          `${location.county} Parish, ${getStateAbbreviation(location.state)}` :
        location.state === 'Alaska' ?
          `${location.county} Borough, ${getStateAbbreviation(location.state)}` :
          `${location.county} County, ${getStateAbbreviation(location.state)}`;
    } else if (isCity) {
      return `${location.city}, ${getStateAbbreviation(location.state)}`;
    } else {
      return location.name;
    }
  };

  const formatLocationNameForSelection = (location) => {
    if (isCounty) {
      return (location.county === 'Carson City' && location.state === 'Nevada') ?
        `${location.county}, ${location.state}` :
        location.state === 'Louisiana' ?
          `${location.county} Parish, ${location.state}` :
        location.state === 'Alaska' ?
          `${location.county} Borough, ${location.state}` :
          `${location.county} County, ${location.state}`;
    } else if (isCity) {
      return `${location.city}, ${location.state}`;
    } else {
      return location.name;
    }
  };

  const getLocationSelectData = (location) => {
    const locationName = formatLocationNameForSelection(location);
    
    return {
      name: locationName,
      type: locationType,
      info: {
        name: locationName,
        type: locationType,
        ...((isCounty || isCity) && { state: location.state })
      }
    };
  };
  
  const sortedData = sortData(data.locations, sortConfig);
  const currentRows = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
  const handleSort = (key) => requestSort(key, sortConfig, onSortChange);
  
  const handleCompareModeChange = (mode) => {
    onCompareModeChange(mode);
    setMenuOpen(false);

    let newScoreField;
    
    if (isCountyOrCity) {
      newScoreField = mode === 'state' 
        ? 'state_composite_score' 
        : 'national_composite_score';
    } else {
      newScoreField = `composite_score`;
    }
    
    onSortChange({
      key: newScoreField,
      direction: 'desc'
    });
  };
  
  const scoreField = isCountyOrCity 
    ? getFieldKey('score')
    : `${crimePrefix}_score`;
    
  const scoreRankField = isCountyOrCity 
    ? getFieldKey('score_rank')
    : `${crimePrefix}_score_rank`;
    
  const compositeScoreField = isCountyOrCity 
    ? getCompositeFieldKey('composite_score')
    : 'composite_score';
    
  const compositeScoreRankField = isCountyOrCity 
    ? getCompositeFieldKey('composite_score_rank')
    : 'composite_score_rank';

  const mainTableTopCrimeLabel =
    selectedCrime === 'all_crime' && selectedSource !== 'BJS_NCVS'
      ? 'All Crime'
      : getCrimeDisplayName(selectedCrime, selectedSource);

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        <div className="table-top-left">
          {showComparisonDropdown ? (
            <div className="comparison-container">
              <div className="comparison-menu-container">
                <button 
                  className={`comparison-menu-button ${menuOpen ? 'menu-open' : ''}`} 
                  onClick={toggleMenu}
                >
                  <ChevronDown size={16} />
                </button>
                {menuOpen && (
                  <div className="comparison-menu">
                    <button 
                      className={compareMode === 'state' ? 'active' : ''}
                      onClick={() => handleCompareModeChange('state')}
                      disabled={compareMode === 'state'}
                    >
                      {stateAbbr} {locationPrefix}
                    </button>
                    <button 
                      className={compareMode === 'national' ? 'active' : ''}
                      onClick={() => handleCompareModeChange('national')}
                      disabled={compareMode === 'national'}
                    >
                      US {locationPrefix}
                    </button>
                  </div>
                )}
              </div>
              <span>
                {compareMode === 'state' ? stateAbbr : 'US'} {locationPrefix} {mainTableTopCrimeLabel} Score Weight: 
                  <strong>
                    {(selectedCrime === 'all_crime' || selectedCrime === 'violent_crime' || selectedCrime === 'property_crime') ? (
                      <span 
                        className="footnote-link" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMethodologyModalOpen(true);
                        }}
                      >
                        &nbsp;{weightedPercentages[selectedCrime]}*
                      </span>
                    ) : (
                      <span>&nbsp;{weightedPercentages[selectedCrime]}</span>
                    )}
                  </strong>
              </span>
            </div>
          ) : (
            <span>{(isAllNational || isOneCountyOrCity || dataIsNational) ? 'US ' : ''}{locationPrefix} {mainTableTopCrimeLabel} Score Weight: 
              <strong>
                {(selectedCrime === 'all_crime' || selectedCrime === 'violent_crime' || selectedCrime === 'property_crime') ? (
                  <span 
                    className="footnote-link" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMethodologyModalOpen(true);
                    }}
                  >
                    &nbsp;{weightedPercentages[selectedCrime]}*
                  </span>
                ) : (
                  <span>&nbsp;{weightedPercentages[selectedCrime]}</span>
                )}
              </strong>
            </span>
          )}
        </div>
      </div>
      <div className="main-table-scroll-container">
        <table className={`main-table location-type-table ${(isCountyOrCity || isMetro) ? locationType : ''} scores`}>
          <thead>
            <tr>
              <th onClick={() => handleSort(compositeScoreRankField)} className="sortable">
                {isCountyOrCity && isMobile ? (
                  <>
                    <div>Rank</div>
                    {getSortIcon(compositeScoreRankField, sortConfig)}
                  </>
                ) : (
                  <div className="stacked-header">
                    <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationPrefix}</div>
                    <div>Rank</div>
                    {getSortIcon(compositeScoreRankField, sortConfig)}
                  </div>
                )}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                {locationPrefix}
                {getSortIcon('name', sortConfig)}
              </th>
              <th onClick={() => handleSort(compositeScoreField)} className="sortable">
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} KRIME Score&trade;</div>
                  <div>(Composite)</div>
                  {getSortIcon(compositeScoreField, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(scoreField)} className="sortable">
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {selectedCrime === 'all_crime' ? 'All ' : ''}{getCrimeDisplayName(selectedCrime)}</div>
                  <div>Score (Non-Weighted)</div>
                  {getSortIcon(scoreField, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(scoreRankField)} className="sortable">
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''}  {locationPrefix} Rank</div>
                  <div>(Non-Weighted)</div>
                  {getSortIcon(scoreRankField, sortConfig)}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((location, index) => (
              <tr key={isCountyOrCity ? `${location[isCounty ? 'county' : 'city']}-${location.state}` : location.name || index}>
                <td className="rank-cell">{formatRank(location[compositeScoreRankField])}</td>
                <td>
                  <span 
                    onMouseEnter={() => setHoveredLocation(formatLocationNameForSelection(location))}
                    onMouseLeave={() => setHoveredLocation(null)}
                    onClick={() => onLocationSelect(getLocationSelectData(location))}
                  >
                    {formatLocationName(location)}
                  </span>
                </td>
                <td className="number-cell centered bold" style={{ color: getScoreColor(location[compositeScoreField]) }}>
                  {formatNumber(location[compositeScoreField], true)}
                </td>
                <td className="number-cell centered bold" style={{ color: getScoreColor(location[scoreField]) }}>
                  <em>{formatNumber(location[scoreField], true)}</em>
                </td>
                <td className="number-cell centered"><em>{formatRank(location[scoreRankField])}</em></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ScoreMethodologyModal 
        isOpen={isMethodologyModalOpen} 
        onClose={() => setIsMethodologyModalOpen(false)} 
      />
    </div>
  );
};

export default LocationTypeScoresTable;